import { Account, GetResponse, Location, RequestParams } from '@commutifi-fe/interfaces';
import { AttributeProps } from '@commutifi/models/Attributes';
import { BookingProps, BookingStatus } from '@commutifi/models/Bookings';
import { BuildingProps } from '@commutifi/models/Buildings';
import { WorksiteProps } from '@commutifi/models/Worksites';
import { CommuterDatasetProps } from '@commutifi/models/CommuterDatasets';
import { EnterpriseDepositProps } from '@commutifi/models/EnterpriseDeposits';
import { EnterpriseNotificationTemplatesProps, EnterpriseNotificationTemplateType, NotificationChannel } from '@commutifi/models/EnterpriseNotificationTemplates';
import { type EnterprisePreTaxResponse } from '@commutifi/models/EnterprisePreTaxResponse';
import { EnterpriseKind, EnterpriseProps, ShippingRates } from '@commutifi/models/Enterprises';
import { FeatureFlagResponse } from '@commutifi/models/FeatureFlag';
import { ImportJobLog } from '@commutifi/models/ImportJobLogs';
import { InventoryProps } from '@commutifi/models/Inventory';
import { InventoryDetailsProps } from '@commutifi/models/InventoryDetails';
import { ServiceInventoryMode } from '@commutifi/models/Service';
import type { MerchantProps } from '@commutifi/models/Merchant';
import { PlanProps } from '@commutifi/models/Plan';
import { IOrganization } from 'shared/interfaces';
import { Primitive, Required } from 'utility-types';
import { JSON_HEADER, apiCall, dashboardApiServer } from '../../index';
import { captureIsPlanValid } from '../../utils/global';
import { PlanPermissionProps } from '../commuterPlans';
import { Submission } from '../compliance/api';
import { InvoiceWithRelations } from '../invoices';
import { PaymentMethod, PostPaymentMethodBody } from '../paymentMethods';
import { AccountWallet, GroupWalletWithMembers, Wallet } from '../wallets';
import { BookingInclude, BookingInventoryStatus } from '@commutifi/constants/Bookings';
import endpoints from './endpoints';
import type { BookingConflictsParams, BookingConflictsResponse, InventoryFilter, PlanOption } from './types';
export { type EnterpriseNotificationTemplatesProps, EnterpriseNotificationTemplateType, NotificationChannel };
export type { BookingConflictsParams, BookingConflictsResponse } from './types';
export type { EnterprisePreTaxResponse };
export type FundingInstructions = {
  type: 'aba' | 'sort_code' | 'iban';
  accountNumber?: string;
  bankName?: string;
  routingNumber?: string;
  accountHolderName?: string;
  sortCode?: string;
  bic?: string;
  country?: string;
  iban?: string;
  reference?: string;
  swift?: string;
};
const apiDashboardServer = apiCall(dashboardApiServer);

// Enterprise accounts: Accounts objects linked to an enterprise
// -----------------------------------------------------------
export const fetchAccounts = (kind: EnterpriseKind, entityId: string, query: Record<string, any>): Promise<GetResponse<Account>> =>
// Use of kind is still needed here as we fetch accounts using account.enterpriseId and account.organizationId
apiDashboardServer(`/${kind === EnterpriseKind.organization ? 'organizations' : 'enterprises'}/${entityId}/accounts`, {
  method: 'get',
  queryParams: query
});
export type ManagerAccount = {
  name: string;
  email: string;
  phoneNumber?: string;
  password: string;
};
export type ManagerEnterprise = {
  name: string;
  address?: {
    formattedAddress: string;
    location: Location;
  };
};
export type ManagerCreationPayload = {
  account: ManagerAccount;
  enterprise: ManagerEnterprise;
  invitationToken: string;
};
type PostManagerApiFunc = (data: ManagerCreationPayload) => Promise<Account>;
export const postManagerAccountEnterprise: PostManagerApiFunc = data => apiDashboardServer(endpoints.POST.Managers.route(), {
  method: 'post',
  data
});
export const importAccounts = (enterpriseId: string) => (form: Blob) => apiDashboardServer(endpoints.POST.ImportAccounts.route(enterpriseId), {
  method: 'post',
  data: form
});
export const postEmployees = (enterpriseId: string) => (data: Record<string, unknown>) => apiDashboardServer(endpoints.POST.EnterpriseAccounts.route(enterpriseId), {
  method: 'post',
  headers: JSON_HEADER,
  data: JSON.stringify(data)
});

// Enterprise finances: balances, invoices and merchants
// ---------------------------------------------
export type EnterpriseBalanceDetails = {
  currentBalance: number;
  spending: number;
  deposits: number;
  pendingDeposits: number;
  totalBalance: number;
};
export const fetchEnterpriseBalances = async (enterpriseId: string,
// To make sure order of params don't change in the future but there is no params supported yet
queryParams: Record<string, never> = {}): Promise<EnterpriseBalanceDetails> => apiDashboardServer(endpoints.GET.EnterpriseBalances.route(enterpriseId), {
  method: 'get',
  queryParams
});
export const fetchEnterpriseInvoices = (enterpriseId: string, queryParams: RequestParams): Promise<GetResponse<InvoiceWithRelations>> => apiDashboardServer(endpoints.GET.EnterpriseInvoices.route(enterpriseId), {
  method: 'get',
  queryParams
});
export const fetchEnterpriseDeposits = (enterpriseId: string, queryParams: RequestParams): Promise<GetResponse<EnterpriseDepositProps>> => apiDashboardServer(endpoints.GET.EnterpriseDeposits.route(enterpriseId), {
  method: 'get',
  queryParams
});
export const getEnterpriseMerchants = (enterpriseId: string, queryParams?: RequestParams): Promise<GetResponse<MerchantProps>> => apiDashboardServer(endpoints.GET.EnterpriseMerchants.route(enterpriseId), {
  method: 'get',
  queryParams
});

// Enterprise administration: Modify existing enterprise, Sub-enterprises, managers, attributes and feature flags
// -------------------------------------------------------------------------------------------------------------
export type PostSubEnterpriseBody = Partial<IOrganization> & {
  /** Pass to create a new building atomically with the enterprise in the same transaction */
  building?: {
    address: string;
    location?: Location;
  };
  name: string;
  managerEmails?: string[];
  notificationTemplate?: EnterpriseNotificationTemplatesProps;
};
export type PostSubEnterpriseResponse = {
  enterprise: IOrganization;
  building?: BuildingProps;
  managers?: {
    accountCreation: {
      accounts: Account[];
      failedAccounts: Account[];
    };
    accountInvitation: {
      accounts: Account[];
      failedAccounts: Account[];
    };
  };
  planPermissions?: PlanPermissionProps[];
};
export type SubEnterpriseProps = EnterpriseProps & {
  accounts?: Partial<Account>[];
  managers?: Partial<Account>[];
  worksites?: WorksiteProps[];
};
export const fetchOrgEnterprises = (id: string, queryParams: RequestParams = {}) => apiDashboardServer(endpoints.GET.SubEnterprises.route(id), {
  method: 'get',
  queryParams
});
export const fetchEnterpriseChildren = (enterpriseId: string, queryParams: RequestParams & {
  id?: string | string[];
}): Promise<GetResponse<SubEnterpriseProps>> => apiDashboardServer(endpoints.GET.EnterpriseChildren.route(enterpriseId), {
  method: 'get',
  queryParams
});
export const postSubEnterprise = (id: string, body: PostSubEnterpriseBody): Promise<PostSubEnterpriseResponse> => apiDashboardServer(endpoints.POST.SubEnterprises.route(id), {
  method: 'post',
  headers: JSON_HEADER,
  data: JSON.stringify(body)
});
export const enterpriseAutocomplete = (queryParams: Record<string, any>) => apiDashboardServer(endpoints.GET.SuggestEnterprises.route(), {
  method: 'get',
  headers: JSON_HEADER,
  queryParams
});
export const postEnterprise = (body: Partial<EnterpriseProps>) => apiDashboardServer(endpoints.POST.Enterprises.route(), {
  method: 'post',
  headers: JSON_HEADER,
  data: JSON.stringify(body)
});
export const patchEnterprise = (enterpriseId: string, body: Partial<IOrganization>): Promise<IOrganization> => apiDashboardServer(endpoints.PATCH.Enterprise.route(enterpriseId), {
  method: 'patch',
  data: body
});
export const fetchFeatureFlags = (enterpriseId: string, queryParams?: Record<string, any>): Promise<GetResponse<FeatureFlagResponse>> => apiDashboardServer(endpoints.GET.EnterpriseFeatureFlags.route(enterpriseId), {
  method: 'get',
  queryParams
});
export const postManagers = (id: string, body: {
  emails: string[];
  notificationTemplate?: EnterpriseNotificationTemplatesProps;
}, queryParams: {
  enterpriseId: string;
}): Promise<Required<PostSubEnterpriseResponse, 'managers'>['managers']> => apiDashboardServer(endpoints.POST.SubEnterpriseManager.route(id), {
  method: 'post',
  headers: JSON_HEADER,
  data: JSON.stringify(body),
  queryParams
});
export const deleteManager = (enterpriseId: string, managerId: string): Promise<void> => apiDashboardServer(endpoints.DELETE.Manager.route(enterpriseId, managerId), {
  method: 'delete'
});
export const fetchAttributes = (enterpriseId: string, queryParams: RequestParams & Partial<Record<keyof AttributeProps, Primitive | Primitive[]>> = {}): Promise<GetResponse<AttributeProps>> => apiDashboardServer(endpoints.GET.EnterpriseAttributes.route(enterpriseId), {
  method: 'get',
  queryParams
});
export const verifyEnterpriseHasManager = (enterpriseId: string) => apiDashboardServer(endpoints.GET.VerifyHasManager.route(EnterpriseKind.enterprise, enterpriseId), {
  method: 'get',
  headers: JSON_HEADER
});
export const verifyOrgHasManager = (orgId: string) => apiDashboardServer(endpoints.GET.VerifyHasManager.route(EnterpriseKind.organization, orgId), {
  method: 'get',
  headers: JSON_HEADER
});

// Enterprise Bookings
// -------------------
export { BookingInventoryStatus } from '@commutifi/constants/Bookings';
export const fetchEnterpriseBookings = (enterpriseId: string, queryParams: RequestParams & {
  planId?: string;
  status?: BookingStatus | BookingStatus[];
  enterpriseId?: string;
  dateRange?: [string, string];
  inventoryReferenceId?: string[];
  inventoryStatus?: BookingInventoryStatus;
  include?: BookingInclude | BookingInclude[];
}): Promise<GetResponse<BookingProps>> => apiDashboardServer(endpoints.GET.EnterpriseBookings.route(enterpriseId), {
  method: 'get',
  queryParams
});
export const fetchEnterpriseTimelineBookings = (enterpriseId: string, queryParams: RequestParams & {
  dateRange?: [string, string];
  inventoryStatus?: BookingInventoryStatus;
  include?: BookingInclude | BookingInclude[];
}): Promise<GetResponse<BookingProps>> => apiDashboardServer(endpoints.GET.EnterpriseTimelineBookings.route(enterpriseId), {
  method: 'get',
  queryParams
});
export const getFundingInstructions = (enterpriseId: string, queryParams?: RequestParams): Promise<FundingInstructions> => apiDashboardServer(endpoints.GET.FundingInstructions.route(enterpriseId), {
  method: 'get',
  queryParams
});
export const getEnterpriseServiceBookingsCount = (enterpriseId: string, serviceId: string, queryParams: Record<string, unknown>): Promise<{
  totalCount: number;
  queryCount: number;
}> => apiDashboardServer(endpoints.GET.EnterpriseServiceBookingsCount.route(enterpriseId, serviceId), {
  method: 'get',
  queryParams
});
export const importBookings = (entityId: string) => (form: FormData, queryParams = {}) => apiDashboardServer(endpoints.POST.ImportBookings.route(entityId), {
  method: 'post',
  data: form,
  queryParams
});

// Enterprise Wallets + Groups Wallets + Wallet enrolments (accounts_wallets)
// -----------------------------------
export const fetchEnterpriseGroupsWallets = async (enterpriseId: string, queryParams: RequestParams & {
  membersCount?: boolean;
  walletId?: string;
}): Promise<GetResponse<Required<GroupWalletWithMembers, 'id'>>> => apiDashboardServer(endpoints.GET.EnterpriseGroupsWallets.route(enterpriseId), {
  method: 'get',
  queryParams
});
export const fetchEnterpriseWallets = (enterpriseId: string, queryParams: RequestParams): Promise<GetResponse<Wallet>> => apiDashboardServer(endpoints.GET.EnterpriseWallets.route(enterpriseId), {
  method: 'GET',
  queryParams
});
export const fetchAccountsWallets = (enterpriseId: string, queryParams: RequestParams & {
  latestOnly?: boolean;
  accountId?: string;
} & Partial<Record<keyof AccountWallet, string | number | string[]>>): Promise<GetResponse<Required<AccountWallet, 'id'>>> => apiDashboardServer(endpoints.GET.EnterpriseWalletEnrolments.route(enterpriseId), {
  method: 'get',
  queryParams
});
export const fetchEnterpriseShippingRates = (enterpriseId: string): Promise<ShippingRates> => apiDashboardServer(endpoints.GET.ShippingRates.route(enterpriseId), {
  method: 'get'
});

// Enterprise Plan Permissions + Plans
// -------------------------------
export enum PlanPermissionOperation {
  Create = 'create',
  Update = 'update',
  Delete = 'delete',
}
export type PostPlanPermissionBody = {
  planId: string;
  enterpriseId: string;
  inventoryAllocation?: number;
  operation?: `${PlanPermissionOperation}`;
}[];
export const postEnterprisePlanPermissions = (enterpriseId: string, data: PostPlanPermissionBody): Promise<PlanPermissionProps> => apiDashboardServer(endpoints.POST.EnterprisePlanPermissions.route(enterpriseId), {
  method: 'POST',
  data
});
export const fetchOneEnterprisePlan = async <T extends Record<string, any> = PlanProps,>(enterpriseId: string, planId: string, queryParams?: RequestParams): Promise<T> => {
  const plan = await apiDashboardServer<T>(endpoints.GET.EnterprisePlan.route(enterpriseId, planId), {
    method: 'get',
    queryParams
  });
  captureIsPlanValid(plan, {
    endpoint: endpoints.GET.EnterprisePlan.route(enterpriseId, planId),
    queryParams
  });
  return plan;
};
export const fetchEnterprisePlans = async (enterpriseId: string, queryParams?: RequestParams & {
  owner?: boolean;
  enterpriseId?: string;
  id?: string | string[];
  'service.inventoryMode'?: ServiceInventoryMode | ServiceInventoryMode[];
}): Promise<GetResponse<PlanProps>> => {
  const res = await apiDashboardServer<Awaited<ReturnType<typeof fetchEnterprisePlans>>>(endpoints.GET.EnterprisePlans.route(enterpriseId), {
    method: 'get',
    queryParams
  });
  res.records = res.records.filter(plan => captureIsPlanValid(plan, {
    endpoint: endpoints.GET.EnterprisePlans.route(enterpriseId),
    queryParams
  }));
  return res;
};
export const fetchEnterprisePlanServiceLatestInventory = async (enterpriseId: string, planId: string, queryParams?: RequestParams): Promise<InventoryProps | null> => apiDashboardServer(endpoints.GET.EnterprisePlanServiceLatestInventory.route(enterpriseId, planId), {
  method: 'get',
  queryParams
});

// Enterprises Payment methods
// ---------------------------
export const postEnterprisePaymentMethods = (enterpriseId: string, data: PostPaymentMethodBody): Promise<PaymentMethod> => apiDashboardServer(endpoints.POST.PaymentMethods.route(enterpriseId), {
  method: 'post',
  data
});
export const setEnterprisePaymentMethodToDefault = (enterpriseId: string, paymentMethodId: string): Promise<PaymentMethod> => apiDashboardServer(endpoints.PATCH.SetEnterprisePaymentMethodDefault.route(enterpriseId, paymentMethodId), {
  method: 'patch'
});
export const fetchEnterprisePaymentMethods = (id: string, queryParams: RequestParams): Promise<GetResponse<PaymentMethod>> => apiDashboardServer(endpoints.GET.EnterprisePaymentMethods.route(id), {
  method: 'get',
  queryParams
});

// Enterprise Notification Templates
// ----------------------------------
export const fetchEnterpriseNotificationTemplate = (enterpriseId: string, queryParams?: NotificationTemplateParams): Promise<GetResponse<EnterpriseNotificationTemplatesProps>> => apiDashboardServer(endpoints.GET.EnterpriseNotificationTemplates.route(enterpriseId), {
  method: 'GET',
  queryParams
});
interface NotificationTemplateParams extends RequestParams {
  type?: string;
}
export const putNotificationTemplate = (enterpriseId: string, body: Partial<EnterpriseNotificationTemplatesProps>): Promise<EnterpriseNotificationTemplatesProps> => apiDashboardServer(endpoints.PUT.EnterpriseNotificationTemplates.route(enterpriseId), {
  method: 'PUT',
  data: body
});

// Enterprise compliance: DDOT submissions
// ----------------------------------------
export const fetchAdminDDOTSubmissions = (orgId: string, queryParams: RequestParams & Partial<Submission>): Promise<GetResponse<Submission>> => apiDashboardServer(endpoints.GET.AdminSubmissions.route(orgId), {
  method: 'get',
  queryParams
});
export const fetchAdminDDOTSubmission = (organizationId: string, submissionId: string, queryParams: RequestParams & Partial<Submission>): Promise<Submission> => apiDashboardServer(endpoints.GET.AdminSubmission.route(organizationId, submissionId), {
  method: 'get',
  queryParams
});
export const patchSubmissionAdmin = (orgId: string, submissionId: string, data: Partial<Omit<Submission, 'id'>>): Promise<Submission> => apiDashboardServer(endpoints.PATCH.SubmissionAdmin.route(orgId, submissionId), {
  method: 'patch',
  headers: JSON_HEADER,
  data
});

// Enterprise MISC. endpoints
// --------------------------
export const sendEmployeeSurveyInvite = (entityId: string, data = {}) => apiDashboardServer(endpoints.PATCH.EnterpriseAccountInvite.route(entityId), {
  method: 'patch',
  headers: JSON_HEADER,
  data: JSON.stringify(data)
});
export const fetchGroups = (entityId: string, queryParams: Record<string, any>) => apiDashboardServer(endpoints.GET.OrganizationLegacyGroups.route(entityId), {
  method: 'get',
  queryParams
});
export const fetchOrganizationMetrics = (id: string, queryParams: {
  enterpriseId?: string;
}) => apiDashboardServer(endpoints.GET.OrganizationMetrics.route(id), {
  method: 'GET',
  queryParams
});

/**
 * The enterprise's white-label dashboard base URL — its SSO subdomain origin when one exists, else
 * the default flat URL. Resolved server-side by the same api-user AppService the invitation emails
 * use, so a copied invite link points at the same origin the email would.
 */
export const fetchEnterpriseDashboardUrl = async (enterpriseId: string): Promise<string | null> => {
  // api-dashboard's UnWrapDataResponse middleware strips the controller's `{ data }` envelope, so
  // the wire body is `{ url }` (not `{ data: { url } }`) — read `url` off the top level.
  const response: {
    url?: string;
  } = await apiDashboardServer(endpoints.GET.EnterpriseDashboardUrl.route(enterpriseId), {
    method: 'GET'
  });
  // react-query rejects `undefined` as query data; normalize the "no subdomain / not resolved" case
  // to `null` so the hook has a concrete value and callers fall back to the current origin.
  return response?.url ?? null;
};
export interface CommuterDatasetResponse extends CommuterDatasetProps {
  importJobLog: ImportJobLog;
}
export const fetchEnterpriseCommuterDatasets = (enterpriseId: string, queryParams: RequestParams): Promise<GetResponse<CommuterDatasetResponse>> => apiDashboardServer(endpoints.GET.EnterpriseCommuterDatasets.route(enterpriseId), {
  method: 'get',
  queryParams
});
export const fetchEnterpriseBookingsFilters = (organizationId: string): Promise<PlanOption[]> => apiDashboardServer(endpoints.GET.EnterpriseBookingsFilters.route(organizationId), {
  method: 'get'
});
export const fetchEnterpriseInventoryFilters = (enterpriseId: string, queryParams: RequestParams = {}): Promise<InventoryFilter[]> => apiDashboardServer(endpoints.GET.EnterpriseInventoryFilters.route(enterpriseId), {
  method: 'get',
  queryParams
});
export const fetchInventoryBookingConflicts = async (enterpriseId: string, serviceId: string, inventoryId: string, queryParams: BookingConflictsParams): Promise<BookingConflictsResponse> => apiDashboardServer(endpoints.GET.EnterprisePlanServiceInventoryBookingConflicts.route(enterpriseId, serviceId, inventoryId), {
  method: 'get',
  queryParams
});
export const fetchEnterpriseServicesFilters = (enterpriseId: string, queryParams: RequestParams = {}): Promise<InventoryFilter[]> => apiDashboardServer(endpoints.GET.EnterpriseServicesFilters.route(enterpriseId), {
  method: 'get',
  queryParams
});
export const fetchEnterpriseServicesInventory = (enterpriseId: string, queryParams: RequestParams = {}): Promise<GetResponse<InventoryProps>> => apiDashboardServer(endpoints.GET.EnterpriseServicesInventory.route(enterpriseId), {
  method: 'get',
  queryParams
});
export const fetchEnterprisePlanServiceInventory = (enterpriseId: string, planId: string, queryParams: RequestParams = {}): Promise<GetResponse<InventoryProps>> => apiDashboardServer(endpoints.GET.EnterprisePlanServiceInventory.route(enterpriseId, planId), {
  method: 'get',
  queryParams
});
export interface InventoryWithRelations extends InventoryProps {
  inventoryDetails?: InventoryDetailsProps;
}
export const fetchEnterpriseServiceInventoryBookingConflicts = (enterpriseId: string, serviceId: string, inventoryId: string, queryParams: BookingConflictsParams): Promise<BookingConflictsResponse> => apiDashboardServer(`/enterprises/${enterpriseId}/services/${serviceId}/inventory/${inventoryId}/booking-conflicts`, {
  method: 'get',
  queryParams
});
export const patchEnterprisePlanServiceInventory = (enterpriseId: string, serviceId: string, inventoryId: string, body: Partial<InventoryProps>): Promise<InventoryProps> => apiDashboardServer(endpoints.PATCH.EnterprisePlanServiceInventory.route(enterpriseId, serviceId, inventoryId), {
  method: 'patch',
  data: body
});

// Enterprise Pre-tax Configuration
// ---------------------------------
export const fetchEnterprisePretax = (enterpriseId: string): Promise<EnterprisePreTaxResponse> => apiDashboardServer(endpoints.GET.EnterprisePretax.route(enterpriseId), {
  method: 'get'
});